/* oxlint-disable @typescript-eslint/consistent-type-definitions */
/**
 * This component is built on top of shadcn's data-table component
 * @see https://ui.shadcn.com/docs/components/data-table
 */

import { useRef } from 'react';

import { flexRender, type Row, type Table as ReactTable, type RowData, type Column } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/tables/common/BaseTable';
import { cn } from '~/common/string/classname';

import type { RowData as OriginalRowData } from '@tanstack/react-table';
import { DataTableColumnHeader } from './DataTableColumnHeader';
import { DataTableSkeletons } from './DataTableSkeletons';
import Link, { type LinkProps } from 'next/link';

export type RowDataWithId = OriginalRowData & { id: string };

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    enableNavigation?: boolean;
    // unused, but needed to satisfy type checker, eslint comment won't work
    _?: TData;
  }

  interface ColumnMeta<TData extends RowData, TValue> {
    filterDisplayValue?: Record<string, string | React.ReactNode>;
    filterDisplayValueFn?: (value: TValue, data: TData) => React.ReactNode;
    skeleton?: () => React.ReactNode;
  }
}

type DataTableOptions = {
  enableVirtualization?: boolean;
  estimatedRowHeight?: number;
  overscan?: number;
};

type DataTableProps<TData> = {
  className?: string;
  classNames?: {
    header?: string;
    cell?: string;
    pinnedCell?: string;
  };
  table: ReactTable<TData>;
  onRowClick?: (row: Row<TData>) => void;
  rowLinkProps?: (row: Row<TData>) => LinkProps;
  emptyMessage?: React.ReactNode;
  noResultsMessage?: React.ReactNode;
  options?: DataTableOptions;
  isLoading?: boolean;
  higlightRow?: (row: Row<TData>) => boolean;
  hideHeaderWhenEmpty?: boolean;
  /** Render expanded content below a row. Return null to skip. */
  renderSubRow?: (row: Row<TData>) => React.ReactNode | null;
};

const getColumnPinningClasses = <TData, TValue>(column: Column<TData, TValue>) => {
  const isPinned = column.getIsPinned();
  return cn({
    'sticky bg-[var(--mantine-color-body)]': isPinned,
  });
};

export function DataTable<TData>({
  className,
  classNames,
  table,
  onRowClick,
  rowLinkProps,
  options,
  emptyMessage,
  noResultsMessage,
  isLoading,
  higlightRow,
  hideHeaderWhenEmpty,
  renderSubRow,
}: DataTableProps<TData>) {
  // Virtualization setup
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { rows } = table.getRowModel();
  const enableVirtualization = options?.enableVirtualization && rows.length > 0;

  return (
    <div
      ref={tableContainerRef}
      className="relative isolate min-h-0 flex-1 overflow-auto"
    >
      <Table className={cn('bg-[var(--mantine-color-body)]', className)}>
        {!(hideHeaderWhenEmpty && !isLoading && (rows == null || rows.length === 0)) && (
          <TableHeader className="sticky top-0 z-20">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'border-b border-gray-200 bg-[var(--mantine-color-body)] dark:border-neutral-700',
                        {
                          'cursor-pointer hover:bg-[var(--mantine-color-default-hover)] hover:bg-none':
                            header.column.getCanSort(),
                          sticky: header.column.getIsPinned(),
                          [classNames?.pinnedCell ?? '']: header.column.getIsPinned(),
                        },
                        className,
                        classNames?.header,
                      )}
                      // Inline styles for dynamic css
                      style={{
                        width: header.getSize() === Number.MAX_SAFE_INTEGER ? 'auto' : header.getSize(),
                        left: header.column.getIsPinned() === 'left' ? header.column.getStart('left') : undefined,
                        right: header.column.getIsPinned() === 'right' ? header.column.getAfter('right') : undefined,
                      }}
                    >
                      <DataTableColumnHeader column={header.column}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </DataTableColumnHeader>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody>
          {isLoading ?
            <DataTableSkeletons columns={table.getAllColumns()} />
          : rows == null || rows.length === 0 ?
            <TableRow className="[&:not(:has(th))]:hover:bg-transparent dark:[&:not(:has(th))]:hover:bg-transparent">
              <TableCell
                colSpan={table.getAllColumns().filter((column) => column.getIsVisible()).length}
                className="h-24 border-none text-center"
              >
                {/* The actual empty message */}
                <div className="absolute inset-0 top-8">
                  {table.getState().globalFilter ?
                    (noResultsMessage ?? <div className="mt-14 flex items-center justify-center">No results.</div>)
                  : (emptyMessage ?? <div className="mt-14 flex items-center justify-center">No results.</div>)}
                </div>

                {/* This is needed to maintain the table layout */}
                <div
                  className="invisible"
                  aria-hidden
                >
                  {emptyMessage ?? 'No results.'}
                </div>
              </TableCell>
            </TableRow>
          : enableVirtualization ?
            <VirtualizedTableRows
              rows={rows}
              tableContainerRef={tableContainerRef}
              options={options}
              onRowClick={onRowClick}
              higlightRow={higlightRow}
              classNames={classNames}
              rowLinkProps={rowLinkProps}
            />
            // Non-virtualized rendering (original behavior)
          : rows.map((row) => {
              const subRow = renderSubRow?.(row) ?? null;
              return (
                <SubRowFragment
                  key={row.id}
                  row={row}
                  onRowClick={onRowClick}
                  higlightRow={higlightRow}
                  classNames={classNames}
                  rowLinkProps={rowLinkProps}
                  subRow={subRow}
                  colSpan={table.getAllColumns().filter((c) => c.getIsVisible()).length}
                />
              );
            })
          }
        </TableBody>
      </Table>
    </div>
  );
}

function TableRowWrapper<TData>({
  row,
  onRowClick,
  higlightRow,
  classNames,
  rowLinkProps,
}: {
  row: Row<TData>;
  onRowClick?: (row: Row<TData>) => void;
  higlightRow?: (row: Row<TData>) => boolean;
  classNames?: { cell?: string; pinnedCell?: string };
  rowLinkProps?: (row: Row<TData>) => LinkProps;
}) {
  return (
    <TableRow
      key={row.id}
      data-state={row.getIsSelected() && 'selected'}
      className={cn('group/row relative', {
        'opacity-50': !row.getCanSelect(),
        'cursor-pointer': onRowClick || rowLinkProps,
        'rounded-md shadow-[inset_0_0_0_2px_rgb(147_197_253),0_0_10px_rgba(96,165,250,0.3)]': higlightRow?.(row),
      })}
      onClick={() => onRowClick?.(row)}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          key={cell.id}
          className={cn(getColumnPinningClasses(cell.column), 'break-words', {
            'z-10': cell.column.getIsPinned(),
            [classNames?.pinnedCell ?? '']: cell.column.getIsPinned(),
            [classNames?.cell ?? '']: !cell.column.getIsPinned(),
            // Remove cell borders when row is highlighted to prevent interference with row-level inset box shadow
            'border-b-0': higlightRow?.(row),
            // Add hover background for pinned cells that only responds to this row's hover
            'group-hover/row:bg-[var(--mantine-color-default-hover)]': cell.column.getIsPinned() && !higlightRow?.(row),
            // Add hover background for selected state
            'group-data-[state=selected]/row:bg-[var(--mantine-color-default-hover)]': cell.column.getIsPinned(),
          })}
          // Inline styles for dynamic css
          style={{
            left: cell.column.getIsPinned() === 'left' ? cell.column.getStart('left') : undefined,
            right: cell.column.getIsPinned() === 'right' ? cell.column.getAfter('right') : undefined,
          }}
        >
          <span className={cn('relative', rowLinkProps ? 'pointer-events-none z-[1]' : 'z-[1]')}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </span>
          {!!rowLinkProps && (
            <Link
              className="absolute inset-0"
              {...rowLinkProps?.(row)}
            />
          )}
        </TableCell>
      ))}
    </TableRow>
  );
}

function SubRowFragment<TData>({
  row,
  onRowClick,
  higlightRow,
  classNames,
  rowLinkProps,
  subRow,
  colSpan,
}: {
  row: Row<TData>;
  onRowClick?: (row: Row<TData>) => void;
  higlightRow?: (row: Row<TData>) => boolean;
  classNames?: { cell?: string; pinnedCell?: string };
  rowLinkProps?: (row: Row<TData>) => LinkProps;
  subRow: React.ReactNode | null;
  colSpan: number;
}) {
  return (
    <>
      <TableRowWrapper
        row={row}
        onRowClick={onRowClick}
        higlightRow={higlightRow}
        classNames={classNames}
        rowLinkProps={rowLinkProps}
      />
      {subRow && (
        <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
          <TableCell
            colSpan={colSpan}
            className="border-b-0 p-0"
          >
            {subRow}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function VirtualizedTableRows<TData>({
  rows,
  tableContainerRef,
  options,
  onRowClick,
  higlightRow,
  classNames,
  rowLinkProps,
}: {
  rows: Row<TData>[];
  tableContainerRef: React.RefObject<HTMLDivElement | null>;
  options: DataTableOptions;
  onRowClick?: (row: Row<TData>) => void;
  higlightRow?: (row: Row<TData>) => boolean;
  classNames?: { cell?: string; pinnedCell?: string };
  rowLinkProps?: (row: Row<TData>) => LinkProps;
}) {
  // 53px is the height of a test row in test/draft page
  const DEFAULT_ROW_HEIGHT = 53;
  const DEFAULT_OVERSCAN = 5;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => options?.estimatedRowHeight ?? DEFAULT_ROW_HEIGHT,
    overscan: options?.overscan ?? DEFAULT_OVERSCAN,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  return (
    <>
      {/* Spacer for virtual items before visible range */}
      {virtualItems.length > 0 && <tr style={{ height: virtualItems[0].start }} />}

      {virtualItems.map((virtualItem) => {
        const row = rows[virtualItem.index];
        return (
          <TableRowWrapper
            key={row.id}
            row={row}
            onRowClick={onRowClick}
            higlightRow={higlightRow}
            classNames={classNames}
            rowLinkProps={rowLinkProps}
          />
        );
      })}

      {/* Spacer for virtual items after visible range */}
      {virtualItems.length > 0 && (
        <tr style={{ height: rowVirtualizer.getTotalSize() - (virtualItems.at(-1)?.end ?? 0) }} />
      )}
    </>
  );
}
